import React from 'react';

interface Column {
  key: string;
  header: string;
  render?: (item: any) => React.ReactNode;
  cellClassName?: string;
}

interface TableProps {
  items: any[];
  columns: Column[];
  selectedItemId?: string | null;
  onItemSelect?: (itemId: string) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  scrollable?: boolean;
  maxItemsPerPage?: number;
  rowClassName?: (item: any) => string;
}

export const Table: React.FC<TableProps> = ({
  items,
  columns,
  selectedItemId,
  onItemSelect,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  scrollable = false,
  maxItemsPerPage = 7,
  rowClassName,
}) => {
  const renderPagination = () => {
    if (!onPageChange) return null;

    const pageNumbers = [];

    // Show first 3 pages (or less if totalPages < 3)
    for (let i = 1; i <= Math.min(3, totalPages); i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`pagination-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (currentPage > 4) {
      pageNumbers.push(<span key="dots1" className="pagination-dots">...</span>);
    }

    if (currentPage > 3 && currentPage < totalPages - 2) {
      pageNumbers.push(
        <button
          key={currentPage}
          className="pagination-number active"
          onClick={() => onPageChange(currentPage)}
        >
          {currentPage}
        </button>
      );
    }

    if (currentPage < totalPages - 3) {
      pageNumbers.push(<span key="dots2" className="pagination-dots">...</span>);
    }

    // Only show last pages if there are more than 3 pages
    if (totalPages > 3) {
      for (let i = Math.max(4, totalPages - 2); i <= totalPages; i++) {
        pageNumbers.push(
          <button
            key={i}
            className={`pagination-number ${currentPage === i ? 'active' : ''}`}
            onClick={() => onPageChange(i)}
          >
            {i}
          </button>
        );
      }
    }

    return (
      <>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <span className="pagination-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8337 10H4.16699M4.16699 10L10.0003 15.8334M4.16699 10L10.0003 4.16669" stroke="#273444"  stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          </span> Previous
        </button>

        <div className="pagination-numbers">
          {pageNumbers}
        </div>

        <button
          className="pagination-btn"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Next <span className="pagination-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="#273444" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
      </>
    );
  };

  const hasPagination = onPageChange;
  const shouldScroll = scrollable && items.length > maxItemsPerPage;

  return (
    <>
      <div className={`table-container ${shouldScroll ? 'table-container-scrollable' : ''}`}>
        <table className="table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className={shouldScroll ? 'table-body-scrollable' : ''}>
            {items.map((item) => {
              const selectedClass = selectedItemId === item.id ? 'selected-row' : '';
              const customClass = rowClassName ? rowClassName(item) : '';
              const combinedClass = [selectedClass, customClass].filter(Boolean).join(' ');

              return (
                <tr
                  key={item.id}
                  className={combinedClass || undefined}
                  onClick={() => onItemSelect?.(item.id)}
                >
                {columns.map((column) => (
                  <td key={column.key} className={column.cellClassName || ''}>
                    {column.render ? column.render(item) : item[column.key]}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasPagination && (
        <div className="pagination-container">
          {renderPagination()}
        </div>
      )}
    </>
  );
};

export type { Column };

